import twitterText from 'twitter-text'
import emojiRegex from 'emoji-regex-xs'
import GraphemeSplitter from 'grapheme-splitter'
import { MastodonParser } from '~publish/legacy/composer/composer/lib/parsers/MastodonParser'
import {
  AttachmentTypes,
  MediaAttachmentTypes,
  type SERVICE_GOOGLEBUSINESS,
  type SERVICE_INSTAGRAM,
  SERVICE_LINKEDIN,
  type SERVICE_STARTPAGE,
  type SERVICE_YOUTUBE,
} from '~publish/legacy/constants'
import { EditorStateProxy } from '../EditorStateProxy/EditorStateProxy'
import type { Draft } from './Draft'
import type {
  ChannelDataProperties,
  GoogleBusinessChannelData,
  InstagramChannelData,
  StartPageChannelData,
  YoutubeChannelData,
} from './types'
import {
  FileUploadConfig,
  getMixedMediaError,
  UploaderRestrictions,
} from '@buffer-mono/uploader'
import { Document } from '../Document'
import { PostTypeCarousel } from '~publish/legacy/post/constants'
import { Service } from '~publish/legacy/constants/services/ServiceDefinitions'

const emojiRegexGlobal = emojiRegex()
const graphemeSplitter = new GraphemeSplitter()

function getChannelDataForChannel(
  draft: Draft,
  channel: typeof SERVICE_GOOGLEBUSINESS,
): GoogleBusinessChannelData | undefined
function getChannelDataForChannel(
  draft: Draft,
  channel: typeof SERVICE_INSTAGRAM,
): InstagramChannelData | undefined
function getChannelDataForChannel(
  draft: Draft,
  channel: typeof SERVICE_STARTPAGE,
): StartPageChannelData | undefined
function getChannelDataForChannel(
  draft: Draft,
  channel: typeof SERVICE_YOUTUBE,
): YoutubeChannelData | undefined
function getChannelDataForChannel(
  draft: Draft,
  channel:
    | typeof SERVICE_GOOGLEBUSINESS
    | typeof SERVICE_INSTAGRAM
    | typeof SERVICE_STARTPAGE
    | typeof SERVICE_YOUTUBE,
): ChannelDataProperties | undefined {
  return draft.channelData?.[channel]
}

export const DraftMethods = {
  hasLinkAttachmentEnabled(draft: Draft): boolean {
    return draft.enabledAttachmentType === AttachmentTypes.LINK
  },

  hasMediaAttachmentEnabled(draft: Draft): boolean {
    return draft.enabledAttachmentType === AttachmentTypes.MEDIA
  },

  hasRetweetAttachmentEnabled(draft: Draft): boolean {
    return draft.enabledAttachmentType === AttachmentTypes.RETWEET
  },

  hasLinkAttachment(draft: Draft): boolean {
    return DraftMethods.hasLinkAttachmentEnabled(draft) && draft.link !== null
  },

  hasLinkAttachmentWithThumbnail(draft: Draft): boolean {
    return (
      DraftMethods.hasLinkAttachmentEnabled(draft) &&
      draft.link?.thumbnail !== null
    )
  },

  hasMediaAttachment(draft: Draft): boolean {
    return (
      (DraftMethods.hasMediaAttachmentEnabled(draft) &&
        draft.images?.length > 0) ||
      draft.video !== null ||
      draft.gif !== null
    )
  },

  hasRetweetAttachment(draft: Draft): boolean {
    return (
      DraftMethods.hasRetweetAttachmentEnabled(draft) && draft.retweet !== null
    )
  },

  getDocumentAttachment(draft: Draft): Document | null {
    return Document.isDocument(draft.document) ? draft.document : null
  },

  getDocumentAttachmentTitle(draft: Draft): string {
    return draft.document?.title ?? ''
  },

  hasDocumentAttachment(draft: Draft): boolean {
    return !!DraftMethods.getDocumentAttachment(draft)
  },

  isLinkedInDocument(draft: Draft): boolean {
    return draft.service.isLinkedin() && draft.updateType === PostTypeCarousel
  },

  shouldShowSuggestedMedia(draft: Draft): boolean {
    return (
      !draft.isVerticalVideoOnlySupported() &&
      !DraftMethods.isLinkedInDocument(draft)
    )
  },

  hasAttachment(draft: Draft): boolean {
    return (
      DraftMethods.hasLinkAttachment(draft) ||
      DraftMethods.hasMediaAttachment(draft) ||
      DraftMethods.hasRetweetAttachment(draft) ||
      DraftMethods.hasDocumentAttachment(draft)
    )
  },

  hasNoMediaAttached(draft: Draft): boolean {
    return (
      draft.images.length === 0 &&
      draft.video === null &&
      draft.gif === null &&
      draft.document === null
    )
  },

  hasVideoAttachment(draft: Draft): boolean {
    return DraftMethods.hasMediaAttachmentEnabled(draft) && draft.video !== null
  },

  hasGifAttachment(draft: Draft): boolean {
    return DraftMethods.hasMediaAttachmentEnabled(draft) && draft.gif !== null
  },

  hasImagesAttachment(draft: Draft): boolean {
    return (
      DraftMethods.hasMediaAttachmentEnabled(draft) && draft.images.length !== 0
    )
  },

  hasText(draft: Draft): boolean {
    return (
      draft.text !== null &&
      draft.text !== undefined &&
      draft.text.trim() !== ''
    )
  },

  hasUnshortenedUrls(draft: Draft): boolean {
    // draftjs clenaup: draft.unshortenedUrls is only used for DraftJS
    const hasUnshortenedUrlsInArray = draft.unshortenedUrls.length > 0

    const hasUnshortenedUrlsInEditorState =
      !!draft.editorState &&
      EditorStateProxy.hasUnshortenedLinks(draft.editorState)

    return hasUnshortenedUrlsInArray || hasUnshortenedUrlsInEditorState
  },

  getChannelDataForChannel,

  getImageCount(draft: Draft): number {
    return draft.images.length
  },

  getMediaCount(draft: Draft): number {
    return (
      draft.images.length +
      (draft.video !== null ? 1 : 0) +
      (draft.gif !== null ? 1 : 0)
    )
  },

  exceedsMaxImageCount(draft: Draft): boolean {
    return (
      this.getImageCount(draft) > draft.service.maxAttachableImagesCount(draft)
    )
  },

  /**
   * Returns the additional amount of characters that need to be
   * added to the character count under specific conditions.
   *
   * Some services require that the character count be adjusted
   * under specific conditions, eg: A post has an image or video attachment.
   *
   * These characters all relate to things outside of the text.
   */
  getAdditionalCharacterCount(draft: Draft, text: string): number {
    if (draft.service.isTwitter()) {
      const mockAttachmentLink = 'https://pbs.twimg.com/media/XXXXXX'
      const mockAttachmentText = ` ${mockAttachmentLink}`

      if (
        draft.enabledAttachmentType === AttachmentTypes.MEDIA &&
        draft.video !== null &&
        draft.service.nativeVideoSizeLimit &&
        draft.service.nativeVideoDurationLimit
      ) {
        const videoSizeLessThanNativeLimit =
          draft.video.size < draft.service.nativeVideoSizeLimit
        const videoDurationLessThanNativeLimit =
          draft.video.durationMs < draft.service.nativeVideoDurationLimit * 1000

        const willVideoBeNativeOnTwitter =
          videoSizeLessThanNativeLimit && videoDurationLessThanNativeLimit
        const willVideoLinkBeAddedToText = !willVideoBeNativeOnTwitter

        if (willVideoLinkBeAddedToText) {
          return twitterText.getTweetLength(mockAttachmentText)
        }
      }
    }

    if (draft.service.isLinkedin()) {
      let charCount = 0

      const urls = twitterText.extractUrls(text)
      const hasLinkAttached =
        draft.enabledAttachmentType === AttachmentTypes.LINK &&
        draft.link !== null
      const isAttachedLinkInText =
        draft?.link && hasLinkAttached && urls.includes(draft.link.url)

      // Only add 24 chars for link attachment if the
      // attached link is not also found in the text
      if (hasLinkAttached && !isAttachedLinkInText) charCount += 24

      // Add 24 chars for images and videos
      if (draft.enabledAttachmentType === AttachmentTypes.MEDIA) {
        if (draft.images?.length > 0) charCount += 24
        if (draft.video !== null) charCount += 24
      }

      return charCount
    }

    if (draft.service.isMastodon()) {
      return draft.channelData?.mastodon?.spoilerText?.length || 0
    }

    return 0
  },

  /**
   * Returns the character count limit for the service
   */
  getCharLimit(draft: Draft): number | null {
    if (typeof draft.service.charLimit === 'function') {
      return draft.service.charLimit(draft)
    }
    return draft.service.charLimit
  },

  /**
   * Returns the character count for text in Bluesky format
   */
  getBlueskyTextCharacterCount(text: string): number {
    // Use grapheme splitter for accurate emoji and character counting
    const graphemes = graphemeSplitter.splitGraphemes(text)
    let charCount = graphemes.length

    const urls = twitterText.extractUrlsWithIndices(text, {
      extractUrlsWithoutProtocol: false,
    })

    // If there are urls in the text, handle each URL's length
    if (urls.length > 0) {
      urls.forEach((urlWithIndices) => {
        try {
          const url = new URL(urlWithIndices.url)

          charCount -= urlWithIndices.url.length

          charCount +=
            url.host.length +
            Math.min(
              url.pathname.replace(/^\/$/, '').length +
                url.search.length +
                url.hash.length,
              16,
            )
        } catch (e) {}
      })
    }

    return charCount
  },

  /**
   * Returns the character count for the given text.
   */
  getTextCharacterCount(draft: Draft, text: string): number {
    if (draft.service.isTwitter()) {
      return twitterText.getTweetLength(text)
    }

    if (draft.service.isBluesky()) {
      return this.getBlueskyTextCharacterCount(text)
    }

    if (draft.service.isInstagram()) {
      const charCount = (text && text.length) || 0
      let lineBreakLength = 0

      const lineBreakMatch = text?.match(/\n/g)
      if (text && lineBreakMatch) {
        lineBreakLength = lineBreakMatch.length
      }
      return charCount + lineBreakLength
    }

    if (draft.service.isLinkedin()) {
      let charCount = (text && text.length) || 0

      const urls = twitterText.extractUrls(text)

      // If there are urls in the text, subtract the url length
      // from the charCount and add back 24 chars for each url
      if (urls.length > 0) {
        let totalUrlLength = 0

        urls.forEach((url) => {
          totalUrlLength += url.length
        })

        const additionalCharCount = 24
        charCount -= totalUrlLength
        charCount += urls.length * additionalCharCount
      }

      return charCount
    }

    if (draft.service.isMastodon()) {
      return MastodonParser.getCharCount(text)
    }

    return (text && text.length) || 0
  },

  /**
   * Returns the text character count plus any additional characters
   * that need to be added under specific conditions;
   * eg: A post has an image or video attachment.
   */
  getFullCharacterCount(draft: Draft, text: string | null | undefined): number {
    if (!text) return 0

    return (
      DraftMethods.getTextCharacterCount(draft, text) +
      DraftMethods.getAdditionalCharacterCount(draft, text)
    )
  },

  getJoinedThreadsText(draft: Draft): string | undefined {
    return draft?.thread
      ?.reduce<string[]>((threadedDraftsText, threadedDraft) => {
        threadedDraftsText.push(threadedDraft.text)
        if (
          threadedDraft?.retweet?.text &&
          threadedDraft.retweet.text.trim() !== ''
        ) {
          threadedDraftsText.push(threadedDraft.retweet.text)
        }
        return threadedDraftsText
      }, [])
      .join('\n')
  },

  getFileAttachmentCount(draft: Draft): number {
    if (
      DraftMethods.hasVideoAttachment(draft) ||
      DraftMethods.hasGifAttachment(draft)
    )
      return 1

    return DraftMethods.hasImagesAttachment(draft) ? draft.images.length : 0
  },

  getUploaderId(draft: Draft): string {
    const { id, updateType } = draft

    const servicesIgnoringUpdateType = [
      // the toggle for carousel is not exposed to the user
      // we need the same uplader id for both posts and carousels
      SERVICE_LINKEDIN,
    ]

    const shouldUseUpdateType =
      !!updateType && !servicesIgnoringUpdateType.includes(id)

    return `composer-uploader-${id}${
      shouldUseUpdateType ? `-${updateType}` : ''
    }`
  },

  getUploaderRestrictions(
    draft: Draft,
    features?: {
      isLinkedInCarouselEnabled?: boolean
    },
  ): UploaderRestrictions {
    // we use MEDIA by default
    let uploadConfig = new Map(FileUploadConfig.MEDIA)

    if (draft.service.isOmni) {
      uploadConfig = new Map(FileUploadConfig.OMNI)
    }

    if (draft.service.isLinkedin() && features?.isLinkedInCarouselEnabled) {
      uploadConfig = new Map(FileUploadConfig.LINKEDIN)
    }

    if (draft.isReelsPost()) {
      uploadConfig = new Map(FileUploadConfig.REELS)
    }

    if (draft.isFacebookReelPost()) {
      uploadConfig = new Map(FileUploadConfig.FACEBOOK_REELS)
    }

    if (draft.isFacebookStoryPost()) {
      uploadConfig = new Map(FileUploadConfig.FACEBOOK_STORIES)
    }

    if (draft.isStoryPost()) {
      uploadConfig = new Map(FileUploadConfig.STORIES)
    }

    if (draft.isShortPost()) {
      uploadConfig = new Map(FileUploadConfig.SHORTS)
    }

    draft.service.unavailableMediaAttachmentTypes.forEach((mediaType) => {
      uploadConfig.delete(mediaType)
    })

    return UploaderRestrictions.new({
      maxNumberOfFiles: draft.service.maxAttachableImagesCount(draft) || 10,
      mixedMediaError: getMixedMediaError({
        supportsGif: draft.service.canHaveMediaAttachmentType(
          MediaAttachmentTypes.GIF,
        ),
        supportsMultipleImages:
          draft.service.maxAttachableImagesCount(draft) > 1,
      }),
      uploadConfig,
    })
  },

  setUpdateType(draft: Draft, updateType: string): void {
    if (!draft.service.allowedPostTypes.includes(updateType)) {
      throw new Error(`Unsupported post type: ${updateType}`)
    }

    draft.updateType = updateType
  },

  canEditLinkAttachment(
    draft: Draft,
    selectedProfiles: Array<{
      id: string
      serviceType: string
      serviceName: string
    }>,
    domainsOwnedByFacebookPages?: Map<string, Map<string, boolean>>,
  ): {
    canEdit: boolean
    isResultCertain: boolean
    hasNonPages?: boolean
    isLoading: boolean
  } {
    // Only Facebook enforces more complex domain ownership checks
    // so for all other services we simply check whether the config allows it
    if (!draft.service.isFacebook()) {
      return {
        canEdit: draft.service.canEditLinkAttachment ?? false,
        isResultCertain: true,
        isLoading: false,
      }
    }
    // This is a defensive check for any services that slip through the first check
    // so we can return early before we do more complex checks
    if (!draft.service.canEditLinkAttachment) {
      return {
        canEdit: false,
        isResultCertain: true,
        isLoading: false,
      }
    }

    // Given that we've now returned for all non-Facebook services
    // and anything that can't edit according to their config
    // we can now focus on Facebook specific checks
    const selectedFacebookProfiles = selectedProfiles.filter(
      (profile) => profile.serviceName === Service.Facebook,
    )

    // If only Facebook Profiles/Groups are selected (not Pages), prevent editing
    const hasOnlyNonPagesSelected = selectedFacebookProfiles.every(
      (profile) => profile.serviceType !== 'page',
    )
    if (hasOnlyNonPagesSelected && !draft.link?.wasEdited) {
      return {
        canEdit: false,
        isResultCertain: true,
        isLoading: false,
      }
    }

    // If Facebook Profiles or Groups are selected, default to the generic info message
    // that mentions their unability to edit link previews
    const hasNonPagesSelected = selectedFacebookProfiles.some(
      (profile) => profile.serviceType !== 'page',
    )

    if (hasNonPagesSelected) {
      return {
        canEdit: true,
        isResultCertain: false,
        hasNonPages: true,
        isLoading: false,
      }
    }

    // Don't check more than 5 profiles to save on Facebook API request quota
    const maxPagesToCheckCount = 5

    // If unsure, allow editing straight away
    if (selectedFacebookProfiles.length > maxPagesToCheckCount) {
      return {
        canEdit: true,
        isResultCertain: false,
        isLoading: false,
      }
    }

    const selectedFacebookProfilesIds = selectedFacebookProfiles.map(
      ({ id }) => id,
    )
    const linkDomainOwnershipData = draft.link?.url
      ? domainsOwnedByFacebookPages?.get(draft.link.url)
      : undefined

    // Default to true while we retrieve more data
    if (linkDomainOwnershipData === undefined) {
      return {
        canEdit: true,
        isResultCertain: false,
        isLoading: true,
      }
    }

    const doAllSelectedProfilesOwnDomain = selectedFacebookProfilesIds.every(
      (id) => linkDomainOwnershipData?.get(id) === true,
    )

    if (doAllSelectedProfilesOwnDomain) {
      return {
        canEdit: true,
        isResultCertain: true,
        isLoading: false,
      }
    }

    const doSomeSelectedProfilesOwnDomain = selectedFacebookProfilesIds.some(
      (id) => linkDomainOwnershipData?.get(id) === true,
    )
    const isSomeOwnershipDataBeingLoaded = selectedFacebookProfilesIds.some(
      (id) => linkDomainOwnershipData?.get(id) === null,
    )

    if (
      doSomeSelectedProfilesOwnDomain ||
      isSomeOwnershipDataBeingLoaded ||
      draft.link?.wasEdited
    ) {
      return {
        canEdit: true,
        isResultCertain: false,
        isLoading: isSomeOwnershipDataBeingLoaded,
      }
    }

    return {
      canEdit: false,
      isResultCertain: true,
      isLoading: false,
    }
  },
}
